import { formElementHelper, formHelper } from '@jht/react-library';
import {
  Grid,
  TableContainer,
  TextField,
  Typography,
  Paper,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Table,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Input,
  useTheme,
  ListItemText,
  Tooltip,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles } from '@material-ui/styles';

const fiveYearsOptions = () =>
  ['1', '2', '3', '4', '5'].map(value => {
    return {
      value: value === '1' ? value + ' year' : value + ' years',
      label: value === '1' ? value + ' year' : value + ' years',
    };
  });

const useStyles = makeStyles(theme => ({
  modalityCheckbox: {
    marginRight: '0.5rem',
    '& .MuiOutlinedInput-input': {
      width: '2.4rem',
      height: '1rem',
      padding: '0.5rem',
      paddingRight: '0.3rem',
    },
  },
  label: {
    display: 'flex',
    alignItems: 'center',
  },
  formControl: {
    margin: 1,
    minWidth: 120,
    maxWidth: 300,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
    height: 'auto',
  },
  noLabel: {
    marginTop: 3,
  },
}));

export const Modality = ({ label, disabled, value, onChange }) => {
  const classes = useStyles();
  return (
    <Grid container>
      <TextField
        variant="outlined"
        className={classes.modalityCheckbox}
        inputProps={{ type: 'number', min: 0 }}
        value={value}
        onChange={onChange}
        disabled={disabled}
        fullWidth={false}
      />
      <Typography className={classes.label} color={disabled ? 'textSecondary' : 'textPrimary'}>
        {label}
      </Typography>
    </Grid>
  );
};

const Information = ({ title, label }: { title: string; label: string }) => {
  return (
    <Tooltip title={<span style={{ fontSize: '0.75rem' }}>{title}</span>} aria-label={label}>
      <InfoOutlinedIcon
        fontSize="small"
        color="secondary"
        style={{ paddingTop: '0.25rem', marginLeft: '0.25rem' }}
      />
    </Tooltip>
  );
};
const SelectedList = ({ selected }: { selected: [] }) => {
  const classes = useStyles();
  return (
    <div className={classes.chips}>
      {selected.map((value, i) => (
        <span key={value + i} style={{ fontSize: '0.875rem', marginRight: '0.25rem' }}>
          {value}
          {i === selected.length - 1 ? '' : ','}
        </span>
      ))}
    </div>
  );
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const modalitiesList = [
  'Elliptical',
  'Lower Body Ascent',
  'Upright Bike',
  'Stepper',
  'Treadmill',
  'Ascent',
  'Hybrid Bike',
  'Recumbent Bike',
  'Climbmill',
];

export const IFITGrid = ({
  value,
  disabled = false,
  onChange,
  facilityNames,
  facilityExistence,
  readOnly = false,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const handleChange = (event, fac, product, areAllSelected) => {
    const allClicked = event.target.value.includes('All');
    let newVal = event.target.value;

    if (allClicked) {
      if (areAllSelected) {
        newVal = [];
      } else {
        newVal = modalitiesList;
      }
    }

    onChange({
      ...value,
      [fac]: {
        ...value[fac],
        [product]: newVal,
      },
    });
  };

  const getStyles = (name, values, theme) => {
    return {
      fontWeight:
        (values || []).indexOf(name) === -1
          ? theme.typography.fontWeightRegular
          : theme.typography.fontWeightMedium,
      height: '2rem',
      padding: '0 0.25rem',
      backgroundColor: 'transparent',
    };
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell key={'facilityLabel'}>Facility</TableCell>
            <TableCell key={'newTouchOrTouchXLLabel'}>
              New Touch/Touch XL
              <Information
                title="Additional Touch/Touch XL consoles for an existing facility being installed on or after September 24th, 2024, will receive iFIT as STANDARD at no additional cost to the customer."
                label="New Touch/Touch XL"
              />
            </TableCell>
            <TableCell key={'upgradeTouchOrTouchXLLabel'}>
              Upgraded Touch/Touch XL
              <Information
                title="Existing Touch/Touch XL consoles installed before September 24th, 2024, can upgrade (OPTIONAL) to receive iFIT for an additional $110USD / unit."
                label="Upgraded Touch/Touch XL"
              />
            </TableCell>
            <TableCell key={'newTouchOrTouchXLBaseLabel'}>
              New Touch Base/Touch XL Base
              <Information
                title="Touch Base or Touch XL Base consoles can upgrade (OPTIONAL) to receive iFIT for an additional $110USD / unit."
                label="New Touch Base/Touch XL Base"
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {facilityNames.map((fac, idx) => {
            const modalityOptions = ['All', ...modalitiesList];
            const isExistingFac = facilityExistence[idx];
            const newTouchOrTouchXL = value[fac]?.newTouchOrTouchXL || [];
            const upgradeTouchOrTouchXL = value[fac]?.upgradeTouchOrTouchXL || [];
            const newTouchOrTouchXLBase = value[fac]?.newTouchOrTouchXLBase || [];
            const allNewTouchOrTouchXLSelected = newTouchOrTouchXL.length === modalitiesList.length;
            const allUpgradeTouchOrTouchXLSelected =
              upgradeTouchOrTouchXL.length === modalitiesList.length;
            const allNewTouchOrTouchXLBaseSelected =
              newTouchOrTouchXLBase.length === modalitiesList.length;

            return (
              <TableRow key={`${fac}/-/row`}>
                <TableCell key={`${fac}/-/name`}>{fac}</TableCell>
                <TableCell key={`${fac}/-/new-touchOrTouchXL`}>
                  {isExistingFac || (!readOnly && newTouchOrTouchXL?.length) ? (
                    <FormControl
                      key={`${fac}/-/new-touchOrTouchXL-select`}
                      className={classes.formControl}
                    >
                      {!newTouchOrTouchXL?.length && (
                        <InputLabel id="demo-mutiple-chip-label">Select</InputLabel>
                      )}
                      <Select
                        labelId="demo-mutiple-chip-label"
                        id="demo-mutiple-chip"
                        multiple
                        value={newTouchOrTouchXL || []}
                        onChange={e =>
                          handleChange(e, fac, 'newTouchOrTouchXL', allNewTouchOrTouchXLSelected)
                        }
                        input={<Input id="select-multiple-chip" />}
                        renderValue={(selected: []) => <SelectedList selected={selected} />}
                        MenuProps={MenuProps}
                        disabled={disabled}
                      >
                        {modalityOptions.map(name => {
                          return (
                            <MenuItem
                              key={name}
                              value={name}
                              style={getStyles(name, newTouchOrTouchXL, theme)}
                            >
                              <Checkbox
                                checked={
                                  allNewTouchOrTouchXLSelected && name === 'All'
                                    ? true
                                    : newTouchOrTouchXL.indexOf(name) > -1
                                }
                              />
                              <ListItemText primary={name} />
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  ) : (
                    <span>--</span>
                  )}
                </TableCell>
                <TableCell key={`${fac}/-/upgrade-touchOrTouchXL`}>
                  {isExistingFac || (!readOnly && upgradeTouchOrTouchXL?.length) ? (
                    <FormControl
                      key={`${fac}/-/upgrade-touchOrTouchXL-select`}
                      className={classes.formControl}
                    >
                      {!upgradeTouchOrTouchXL?.length && (
                        <InputLabel id="demo-mutiple-chip-label">Select</InputLabel>
                      )}
                      <Select
                        labelId="demo-mutiple-chip-label"
                        id="demo-mutiple-chip"
                        multiple
                        value={upgradeTouchOrTouchXL || []}
                        onChange={e =>
                          handleChange(
                            e,
                            fac,
                            'upgradeTouchOrTouchXL',
                            allUpgradeTouchOrTouchXLSelected,
                          )
                        }
                        input={<Input id="select-multiple-chip" />}
                        renderValue={(selected: []) => <SelectedList selected={selected} />}
                        MenuProps={MenuProps}
                        disabled={disabled}
                      >
                        {modalityOptions.map(name => (
                          <MenuItem
                            key={name}
                            value={name}
                            style={getStyles(name, upgradeTouchOrTouchXL, theme)}
                          >
                            <Checkbox
                              checked={
                                allUpgradeTouchOrTouchXLSelected && name === 'All'
                                  ? true
                                  : upgradeTouchOrTouchXL.indexOf(name) > -1
                              }
                            />
                            <ListItemText primary={name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <span>--</span>
                  )}
                </TableCell>
                <TableCell key={`${fac}/-/new-touchOrTouchXLBase`}>
                  {!readOnly || (readOnly && newTouchOrTouchXLBase?.length) ? (
                    <FormControl
                      key={`${fac}/-/new-TouchOrTouchXLBase-select`}
                      className={classes.formControl}
                    >
                      {!newTouchOrTouchXLBase?.length && (
                        <InputLabel id="demo-mutiple-chip-label">Select</InputLabel>
                      )}
                      <Select
                        labelId="demo-mutiple-chip-label"
                        id="demo-mutiple-chip"
                        multiple
                        value={newTouchOrTouchXLBase || []}
                        onChange={e =>
                          handleChange(
                            e,
                            fac,
                            'newTouchOrTouchXLBase',
                            allNewTouchOrTouchXLBaseSelected,
                          )
                        }
                        input={<Input id="select-multiple-chip" />}
                        renderValue={(selected: []) => <SelectedList selected={selected} />}
                        MenuProps={MenuProps}
                        disabled={disabled}
                      >
                        {modalityOptions.map(name => (
                          <MenuItem
                            key={name}
                            value={name}
                            style={getStyles(name, newTouchOrTouchXLBase, theme)}
                          >
                            <Checkbox
                              checked={
                                allNewTouchOrTouchXLBaseSelected && name === 'All'
                                  ? true
                                  : newTouchOrTouchXLBase.indexOf(name) > -1
                              }
                            />
                            <ListItemText primary={name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <span>--</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const Facility360Grid = ({ value, disabled = false, onChange, facilityNames }) => {
  const classes = useStyles();
  const numColumns = [
    { field: 'fac360TouchscreenCount', label: 'Touchscreen Count' },
    { field: 'fac360LEDCount', label: 'LED Count' },
    { field: 'fac360SensorCount', label: 'Cardio/Strength Sensor Hardware Count' },
  ];
  const onGridChange = (newVal, fac, field) => {
    const updated = {
      ...value,
      [fac]: {
        ...value[fac],
        [field]: newVal,
      },
    };
    if (updated[fac].fac360Gateway && field === 'fac360SensorCount' && newVal === '0') {
      updated[fac].fac360Gateway = false;
    }
    onChange(updated);
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Facility</TableCell>
            {numColumns.map(col => (
              <TableCell key={`${col.label}Label`}>{col.label}</TableCell>
            ))}
            <TableCell>Gateway Needed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {facilityNames.map(fac => (
            <TableRow key={`${fac}/-/row`}>
              <TableCell key={`${fac}/-/name`}>{fac}</TableCell>
              {numColumns.map(col => (
                <TableCell key={`${fac}/-/${col.field}`}>
                  <TextField
                    variant="outlined"
                    className={classes.modalityCheckbox}
                    inputProps={{ type: 'number', min: 0 }}
                    key={`${fac}/-/${col.field}-input`}
                    value={value[fac][col.field] ?? 0}
                    onChange={evt => onGridChange(evt.target.value, fac, col.field)}
                    disabled={disabled}
                    fullWidth={false}
                  />
                </TableCell>
              ))}
              <TableCell key={`${fac}/-/fac360Gateway`}>
                <Checkbox
                  key={`${fac}/-/fac360Gateway-checkbox`}
                  checked={value[fac].fac360Gateway || false}
                  onChange={evt => onGridChange(evt.target.checked, fac, 'fac360Gateway')}
                  disabled={
                    disabled ||
                    !value[fac].fac360SensorCount ||
                    value[fac].fac360SensorCount === '0'
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const AMVisionGrid = ({ value, disabled = false, onChange, facilityNames }) => {
  const classes = useStyles();
  const numColumns = [
    { field: 'amVisionTouchscreenCount', label: 'Touchscreen Count' },
    { field: 'amVisionLEDCount', label: 'LED Count' },
  ];
  const onGridChange = (newVal, fac, field) => {
    const updated = {
      ...value,
      [fac]: {
        ...value[fac],
        [field]: newVal,
      },
    };
    if (updated[fac].fac360Gateway && field === 'fac360SensorCount' && newVal === '0') {
      updated[fac].fac360Gateway = false;
    }
    onChange(updated);
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Facility</TableCell>
            {numColumns.map(col => (
              <TableCell key={`${col.label}Label`}>{col.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {facilityNames.map(fac => (
            <TableRow key={`${fac}/-/row`}>
              <TableCell key={`${fac}/-/name`}>{fac}</TableCell>
              {numColumns.map(col => (
                <TableCell key={`${fac}/-/${col.field}`}>
                  <TextField
                    variant="outlined"
                    className={classes.modalityCheckbox}
                    inputProps={{ type: 'number', min: 0 }}
                    key={`${fac}/-/${col.field}-input`}
                    value={value[fac][col.field] ?? 0}
                    onChange={evt => onGridChange(evt.target.value, fac, col.field)}
                    disabled={disabled}
                    fullWidth={false}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const productSubforms = {
  AM: () => {
    return formHelper([]);
  },
  AM_VISION: (values, facilityNames) => {
    const form = formHelper([
      formElementHelper({
        fullWidth: true,
        name: 'facility360HelperText',
        component: () => {
          return (
            <>
              <Typography variant="body1" style={{ color: 'rgba(0, 0, 0, 1)' }}>
                Note:
              </Typography>
              <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                The Wi-Fi module is necessary for LED consoles to connect to Asset Management.
              </Typography>
              <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                Enter at least one console (Touchscreen or LED) to continue.
              </Typography>
            </>
          );
        },
      }),
      formElementHelper({
        fullWidth: true,
        value: values,
        name: 'amVisionGrid',
        facilityNames: facilityNames,
        component: props => <AMVisionGrid {...props} />,
      }),
    ]);
    return {
      elements: form.elements.map(el => {
        return { ...el, valid: true };
      }),
      valid: true,
    };
  },
  FACILITY360: (values, facilityNames) => {
    const form = formHelper([
      formElementHelper({
        fullWidth: true,
        name: 'facility360HelperText',
        component: () => {
          return (
            <>
              <Typography variant="body1" style={{ color: 'rgba(0, 0, 0, 1)' }}>
                Note:
              </Typography>
              <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                A facility using Facility 360 powered by Ecofit Cardio/Strength should only have 1
                gateway per facility.
              </Typography>
            </>
          );
        },
      }),
      formElementHelper({
        fullWidth: true,
        value: values,
        name: 'facility360Grid',
        facilityNames: facilityNames,
        component: props => <Facility360Grid {...props} />,
      }),
    ]);
    return {
      elements: form.elements.map(el => {
        return { ...el, valid: true };
      }),
      valid: true,
    };
  },
  FACILITY_CONCIERGE: () => {
    return formHelper([]);
  },
  COM360: values => {
    return formHelper([
      formElementHelper({
        col: 6,
        name: 'c360Type',
        label: 'Community 360 App Type',
        value: values.c360Type,
        inputType: 'select',
        type: 'text',
        options: [
          { value: 'c360_basic_app', label: 'Community 360 - Basic' },
          { value: 'c360_advanced_app', label: 'Community 360 - Advanced' },
          { value: 'c360_pro_app', label: 'Community 360 - Pro' },
        ],
        required: true,
        validation: { required: true },
      }),
      formElementHelper({
        col: 6,
        name: 'orderDuration',
        label: 'Order Duration',
        value: values.orderDuration,
        inputType: 'select',
        type: 'text',
        options: fiveYearsOptions(),
        required: true,
        validation: { required: true },
      }),
    ]);
  },
  ENG360: values => {
    return formHelper([
      formElementHelper({
        col: 6,
        name: 'e360Type',
        label: 'Engage 360 Type',
        value: values.e360Type,
        inputType: 'select',
        type: 'text',
        options: [
          { value: 'engage_360_advanced', label: 'Engage 360 Advanced' },
          { value: 'engage_360_pro', label: 'Engage 360 Pro' },
        ],
        required: true,
        validation: { required: true },
      }),
      formElementHelper({
        col: 6,
        name: 'orderDuration',
        label: 'Order Duration',
        value: values.orderDuration,
        inputType: 'select',
        type: 'text',
        options: fiveYearsOptions(),
        required: true,
        validation: { required: true },
      }),
    ]);
  },
  EGYM: values => {
    const form = formHelper([
      formElementHelper({
        col: 6,
        name: 'egymClientId',
        label: 'EGYM Client ID',
        value: values.egymClientId,
        inputType: 'text',
        type: 'text',
        required: false,
      }),
      formElementHelper({
        col: 6,
        name: 'egymClientSecret',
        label: 'EGYM Client Secret',
        value: values.egymClientSecret,
        inputType: 'text',
        type: 'text',
        required: false,
      }),
    ]);

    return {
      elements: form.elements.map(el => {
        return { ...el, valid: true };
      }),
      valid: true,
    };
  },
  PACE: values => {
    const regionOptions = [
      { value: 'european', label: 'European' },
      { value: 'united_states', label: 'United States' },
    ];
    // Only include the selected partner's fields so hidden ones don't reserve
    // empty grid columns (the form library renders a Grid item per element).
    const partnerFields = {
      egym: [
        formElementHelper({
          col: 6,
          name: 'egymClientId',
          label: 'EGYM Client ID',
          value: values.egymClientId,
          inputType: 'text',
          type: 'text',
          required: false,
        }),
        formElementHelper({
          col: 6,
          name: 'egymClientSecret',
          label: 'EGYM Client Secret',
          value: values.egymClientSecret,
          inputType: 'text',
          type: 'text',
          required: false,
        }),
        formElementHelper({
          col: 6,
          name: 'egymTenantId',
          label: 'Tenant ID',
          value: values.egymTenantId,
          inputType: 'text',
          type: 'text',
          required: false,
          validation: { regex: /^[a-zA-Z0-9_]*$/ },
          validationMessages: { regex: 'Tenant ID contains invalid characters' },
        }),
        formElementHelper({
          col: 6,
          name: 'egymTenantRegion',
          label: 'Tenant Region',
          value: values.egymTenantRegion,
          inputType: 'select',
          type: 'text',
          options: regionOptions,
          required: false,
          validation: { required: false },
        }),
      ],
      magicline: [
        formElementHelper({
          col: 6,
          name: 'magiclineApiKey',
          label: 'API Key',
          value: values.magiclineApiKey,
          inputType: 'text',
          type: 'text',
          required: false,
          validation: { regex: /^[a-zA-Z0-9]*$/ },
          validationMessages: { regex: 'API Key must be alphanumeric' },
        }),
        formElementHelper({
          col: 6,
          name: 'magiclineBaseUrl',
          label: 'Base URL',
          value: values.magiclineBaseUrl,
          inputType: 'text',
          type: 'text',
          required: false,
          validation: { regex: /^https?:\/\/[^\s/$.?#].\S*$/i },
          validationMessages: {
            regex: 'Base URL must be a valid URL starting with http:// or https://',
          },
        }),
        formElementHelper({
          col: 6,
          name: 'magiclineTenantId',
          label: 'Tenant ID',
          value: values.magiclineTenantId,
          inputType: 'text',
          type: 'text',
          required: false,
          validation: { regex: /^[a-zA-Z0-9_]*$/ },
          validationMessages: { regex: 'Tenant ID contains invalid characters' },
        }),
        formElementHelper({
          col: 6,
          name: 'magiclineTenantRegion',
          label: 'Tenant Region',
          value: values.magiclineTenantRegion,
          inputType: 'select',
          type: 'text',
          options: regionOptions,
          required: false,
          validation: { required: false },
        }),
      ],
      matrix: [
        formElementHelper({
          col: 6,
          name: 'matrixTenantId',
          label: 'Tenant ID',
          value: values.matrixTenantId,
          inputType: 'text',
          type: 'text',
          required: false,
          validation: { regex: /^[a-zA-Z0-9_]*$/ },
          validationMessages: { regex: 'Tenant ID contains invalid characters' },
        }),
        formElementHelper({
          col: 6,
          name: 'matrixTenantRegion',
          label: 'Tenant Region',
          value: values.matrixTenantRegion,
          inputType: 'select',
          type: 'text',
          options: regionOptions,
          required: false,
          validation: { required: false },
        }),
      ],
    };

    const form = formHelper([
      formElementHelper({
        col: 6,
        name: 'integrationPartner',
        label: 'Integration Partner',
        value: values.integrationPartner,
        inputType: 'select',
        type: 'text',
        options: [
          // { value: 'egym', label: 'EGYM' }, //TODO: removed as part of DO-275. uncomment when PO wants in the future.
          { value: 'magicline', label: 'Magicline' },
          { value: 'matrix', label: 'Matrix' },
        ],
        required: false,
        validation: { required: false },
      }),
      ...(partnerFields[values.integrationPartner] || []),
    ]);

    return {
      elements: form.elements.map(el => {
        return { ...el, valid: true };
      }),
      valid: true,
    };
  },
  ECOFIT: values => {
    return formHelper([
      formElementHelper({
        fullWidth: true,
        component: () => (
          <>
            <Typography variant="body1" style={{ color: 'rgba(0, 0, 0, 1)', marginTop: '1rem' }}>
              Note:
            </Typography>
            <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
              Please only check this box and order Ecofit for international markets needing an
              integration with Ecofit <b>(This is not the same as Facility 360)</b>.
            </Typography>
          </>
        ),
      }),
    ]);
  },
  YMCA360: values => {
    return formHelper([
      formElementHelper({
        fullWidth: true,
        component: () => (
          <>
            <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
              Integration is facility-wide (all equipment)
            </Typography>
            <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
              No cost associated with activation
            </Typography>
          </>
        ),
      }),
    ]);
  },
  iFIT: (values, facilityNames, facilityExistence) => {
    const form = formHelper([
      formElementHelper({
        fullWidth: true,
        name: 'iFitHelperTest',
        component: () => {
          return (
            <>
              <Typography variant="body1" style={{ color: 'rgba(0, 0, 0, 1)', marginTop: '1rem' }}>
                Note:
              </Typography>
              {facilityExistence.includes(false) && (
                <Typography
                  variant="body2"
                  style={{ color: 'rgba(0, 0, 0, 0.75)', marginBottom: '0.5rem' }}
                >
                  Select the scenario that applies for this existing facilities order below: Note -
                  You can select more than one option if required, but this will be rare.
                </Typography>
              )}
              <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                iFIT is automatically included for this new facility order unless this is for Touch
                Base/ Touch XL Base. If this is a Touch Base/Touch XL Base order that needs iFIT,
                select modalities in the Touch Base/Touch XL Base column to enable iFIT ordering.
              </Typography>
            </>
          );
        },
        show: facilityExistence.includes(false),
      }),
      formElementHelper({
        fullWidth: true,
        value: values,
        name: 'iFitGrid',
        facilityNames: facilityNames,
        facilityExistence: facilityExistence,
        component: props => <IFITGrid {...props} />,
      }),
    ]);
    return {
      elements: form.elements.map(el => {
        return { ...el, valid: true };
      }),
      valid: true,
    };
  },
  other: values => {
    return formHelper([
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: 'integrationName',
        label: 'Integration Name',
        value: values.integrationName,
        inputType: 'input',
        type: 'text',
        required: true,
        validation: { required: true },
      }),
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: 'integrationDetails',
        label: 'Integration Details',
        value: values.integrationDetails,
        inputType: 'multiline',
        rows: '5',
        variant: 'outlined',
        type: 'text',
        required: false,
      }),
    ]);
  },
};
